import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { toast } from 'sonner';
import { SettingsCard, SettingsGrid, SettingsHelp, SettingsPage } from '../../components/settings/SettingsPage';
import type { ApiResponse } from '../../types/api-manifest';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

/**
 * Payment terms, in the vocabulary an invoice PRINTS.
 *
 * `companies.payment_terms` is free text, and the invoice create paths copy it
 * onto the invoice verbatim (customer terms first, then this), where the PDF,
 * the email and the agent all print it as written. The due date is derived
 * from the first number in it. So the stored value is the rendered value: the
 * API default is 'Net 30', the customers table and all 90 invoices in
 * production use this spelling, and the old options here (`net_30`, …) would
 * have printed "net_30" on a customer's invoice.
 */
const PAYMENT_TERMS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60'] as const;

/**
 * The fields this page shows, and so the only ones it may send.
 *
 * NOT HERE, ON PURPOSE: `show_bank_details`. The PATCH accepts it and nothing
 * reads it — no invoice template, no email, no PDF. It is left off the page and
 * out of every save, so a stored value stays exactly as it is.
 */
type InvoiceNumberMode = 'sequential' | 'match_ticket';

interface InvoiceForm {
  invoice_prefix: string;
  invoice_next_number: number;
  /**
   * companies.invoice_number_mode (api 0620). 'match_ticket' gives an invoice
   * built from exactly ONE field ticket that ticket's number under the invoice
   * prefix; every other invoice keeps the counter above. The rule, the -A/-B
   * re-invoice suffixes and the fallbacks live in the API
   * (src/lib/invoice-number.ts) — this page only flips the setting.
   */
  invoice_number_mode: InvoiceNumberMode;
  payment_terms: string | null;
  invoice_notes: string;
}

export default function InvoiceSettingsPage() {
  const queryClient = useQueryClient();
  // PATCH /settings/invoice is non-strict requireRole('admin').
  const canWrite = useCanWriteCompanySettings();

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'invoice'],
    queryFn: () => apiClient.get('/settings/invoice').then(r => unwrap<Record<string, any>>(r.data) ?? {}),
  });

  const [form, setForm] = useState<InvoiceForm>({
    invoice_prefix: 'INV-',
    invoice_next_number: 1,
    invoice_number_mode: 'sequential',
    payment_terms: null,
    invoice_notes: '',
  });
  // Only the fields the user moved are sent. Re-sending the next number on
  // every save would run the API's reuse guard against a value nobody changed.
  const [touched, setTouched] = useState<Set<keyof InvoiceForm>>(new Set());
  const dirty = touched.size > 0;
  const [nextNumberError, setNextNumberError] = useState('');

  useEffect(() => {
    if (data) {
      setForm(prev => ({
        ...prev,
        invoice_prefix: data.invoice_prefix ?? prev.invoice_prefix,
        invoice_next_number: data.invoice_next_number ?? prev.invoice_next_number,
        invoice_number_mode: data.invoice_number_mode === 'match_ticket' ? 'match_ticket' : 'sequential',
        payment_terms: data.payment_terms ?? null,
        invoice_notes: data.invoice_notes ?? '',
      }));
      setTouched(new Set());
    }
  }, [data]);

  const update = <K extends keyof InvoiceForm>(key: K, value: InvoiceForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setTouched(prev => new Set(prev).add(key));
    if (key === 'invoice_next_number') setNextNumberError('');
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      for (const key of Array.from(touched)) payload[key] = form[key];
      return apiClient.patch('/settings/invoice', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'invoice'] });
      setTouched(new Set());
      toast.success('Invoice settings saved');
    },
    onError: (err: any) => {
      if (err?.response?.status === 409) {
        setNextNumberError('That number is at or below an already-issued invoice — pick a higher value');
      } else {
        toast.error(err?.response?.data?.error || 'Failed to save');
      }
    },
  });

  // The ticket numbering, so the preview shows THIS tenant's tickets: FT-00005
  // on the default sequential padding of 5, FT-0005 on 4, FT-4290-1 on
  // job-derived numbering. Until it arrives the preview is not drawn — a guessed
  // "FT-0005" would be a confident example of a number this tenant never issues.
  const { data: ticketSettings } = useQuery({
    queryKey: ['settings', 'field-tickets'],
    queryFn: () => apiClient.get<ApiResponse<'GET /api/v1/settings/field-tickets'>>('/settings/field-tickets')
      .then(r => unwrap<Record<string, any>>(r.data) ?? {}),
  });
  const ticketPrefix: string | undefined = ticketSettings?.ticket_number_prefix ?? undefined;
  const ticketPadding = Number(ticketSettings?.ticket_number_padding);
  const sampleBody: string | undefined =
    ticketSettings?.ticket_number_mode === 'derived_from_job' ? '4290-1'
    : ticketSettings?.ticket_number_mode === 'sequential' && Number.isInteger(ticketPadding) && ticketPadding > 0
      ? '5'.padStart(ticketPadding, '0')
      : undefined;
  // The API prints the default prefix when the stored one is blank.
  const previewInvoicePrefix = form.invoice_prefix || 'INV-';
  const matchTicket = form.invoice_number_mode === 'match_ticket';
  // An API that predates 0620 sends no mode. Drawing the switch OFF would state
  // a setting the server does not have, so the row is not drawn at all.
  const modeKnown = !!data && typeof data.invoice_number_mode === 'string';

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  // A select cannot show a value it has no option for — it shows the first
  // one while state keeps the real value. A stored spelling outside the list
  // (two tenants hold `net_30`) gets its own option, labelled as what it is.
  const storedOffList = form.payment_terms != null && !(PAYMENT_TERMS as readonly string[]).includes(form.payment_terms);

  return (
    // The Finance & region workspace renders the title and the tab strip.
    <SettingsPage>
      {canWrite ? (
        <div className="flex justify-end">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || saveMutation.isPending}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
            }`}
          >
            {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      ) : (
        <SettingsHelp>Only an admin can change invoice settings.</SettingsHelp>
      )}

      <SettingsGrid>
        <SettingsCard title="Numbering">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="invoice_prefix" className="text-sm font-medium text-white block mb-1">Invoice Prefix</label>
              <input id="invoice_prefix" className={inputCls} value={form.invoice_prefix} disabled={!canWrite} onChange={e => update('invoice_prefix', e.target.value)} placeholder="INV-" />
            </div>
            <div>
              <label htmlFor="invoice_next_number" className="text-sm font-medium text-white block mb-1">Next Invoice Number</label>
              <input id="invoice_next_number" type="number" min="1" className={inputCls} value={form.invoice_next_number} disabled={!canWrite} onChange={e => update('invoice_next_number', parseInt(e.target.value) || 1)} />
              <SettingsHelp className="mt-1">
                Preview: <span className="text-fieldiq-accent font-mono">{form.invoice_prefix}{String(form.invoice_next_number).padStart(5, '0')}</span>
              </SettingsHelp>
              {nextNumberError && <p className="text-xs text-red-400 mt-1">{nextNumberError}</p>}
            </div>
          </div>

          {modeKnown && (
          <div className="flex items-start justify-between gap-4 border-t border-fieldiq-border pt-4">
            <div className="flex-1">
              <p id="match_ticket_label" className="text-sm font-medium text-white">Match the field ticket number</p>
              <SettingsHelp className="mt-0.5">
                An invoice made from one field ticket takes that ticket's number. Invoices for several
                tickets, manual invoices and credit notes keep using the next invoice number above, which
                moves past every matched number. If a number is already used, as when a ticket is invoiced
                again, -A then -B is added. Invoices already issued keep their numbers.
              </SettingsHelp>
              {matchTicket && ticketPrefix && sampleBody && (
                <div data-testid="match-ticket-preview">
                  <SettingsHelp className="mt-1">
                    Preview: <span className="font-mono">{ticketPrefix}{sampleBody}</span> →{' '}
                    <span className="text-fieldiq-accent font-mono">{previewInvoicePrefix}{sampleBody}</span>
                  </SettingsHelp>
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={matchTicket}
              aria-labelledby="match_ticket_label"
              disabled={!canWrite}
              onClick={() => update('invoice_number_mode', matchTicket ? 'sequential' : 'match_ticket')}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-60 ${matchTicket ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${matchTicket ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          )}
        </SettingsCard>

        <SettingsCard title="Terms">
          <div>
            <label htmlFor="payment_terms" className="text-sm font-medium text-white block mb-1">Payment Terms</label>
            <select
              id="payment_terms"
              className={inputCls}
              value={form.payment_terms ?? ''}
              disabled={!canWrite}
              onChange={e => update('payment_terms', e.target.value)}
            >
              {form.payment_terms == null && <option value="" disabled>—</option>}
              {storedOffList && <option value={form.payment_terms!}>{form.payment_terms}</option>}
              {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <SettingsHelp className="mt-1">
              {storedOffList
                ? <>Printed on invoices exactly as shown, “{form.payment_terms}”; pick a value from the list to change it.</>
                : <>Printed on invoices for customers without their own terms.</>}
            </SettingsHelp>
          </div>

          <div>
            <label htmlFor="invoice_notes" className="text-sm font-medium text-white block mb-1">Invoice Notes / Terms</label>
            <textarea id="invoice_notes" className={inputCls + ' min-h-[80px]'} value={form.invoice_notes} disabled={!canWrite} onChange={e => update('invoice_notes', e.target.value)}
              placeholder="Thank you for your business. Payment due within 30 days." />
          </div>
        </SettingsCard>
      </SettingsGrid>
    </SettingsPage>
  );
}
